<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';
import {
  SIDEBAR_MENU_ITEM_ACTIVE,
  SIDEBAR_MENU_ITEM_BASE,
  SIDEBAR_MENU_ITEM_INACTIVE,
  SIDEBAR_MENU_ICON_ACTIVE,
  SIDEBAR_MENU_ICON_INACTIVE,
} from './sidebarMenuItemClasses';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  rightIcon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="[
      SIDEBAR_MENU_ITEM_BASE,
      isActive && !hasActiveChild
        ? SIDEBAR_MENU_ITEM_ACTIVE
        : SIDEBAR_MENU_ITEM_INACTIVE,
    ]"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon
        v-if="icon"
        :icon="icon"
        class="size-5"
        :class="
          isActive || hasActiveChild
            ? SIDEBAR_MENU_ICON_ACTIVE
            : SIDEBAR_MENU_ICON_INACTIVE
        "
      />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-[var(--sidebar-accent)] absolute rounded-full border border-white"
      />
    </div>
    <div class="flex items-center gap-3 flex-grow min-w-0">
      <span class="truncate">
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="rounded-md h-5 flex items-center justify-center text-xxs font-semibold my-0 mx-1 px-1 py-0 min-w-[15px] capitalize flex-shrink-0 outline outline-1"
        :class="{
          'text-[var(--sidebar-accent)] bg-[var(--sidebar-active-bg)] outline-[color-mix(in_srgb,var(--sidebar-accent)_30%,transparent)]':
            isActive,
          'text-n-slate-12 outline-n-strong dark:text-n-slate-11': !isActive,
        }"
      >
        {{ count }}
      </span>
    </div>
    <Icon
      v-if="rightIcon && !expandable"
      :icon="rightIcon"
      class="size-3 text-n-slate-10 flex-shrink-0"
    />
    <span
      v-if="expandable"
      class="size-3.5 text-n-slate-11 flex-shrink-0"
      :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
